import React from 'react';
import { tr } from '../../i18n/i18n';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../../constants';
import { AppButton } from '../atoms';
import { SEVERITY_STYLES } from '../../constant/main.constant';
import { useTranslation } from 'react-i18next';
import { SUBSCRIPTION_LOCK_CODES, SubscriptionLockCode } from '../../utility/subscriptionLock';
import { formatCurrency } from '../../utility/currencyFormatter';

interface ExpiredSubscriptionOverlayProps {
  canManageBilling: boolean;
  unpaidInvoiceCount: number;
  outstandingAmount: number;
  className?: string;
  /** Why the organization is locked (CVA-4668). Defaults to unpaid invoices. */
  code?: SubscriptionLockCode | null;
  /**
   * Render in place of the page instead of floating over it (CVA-4668). The
   * lock screen uses this: the page underneath is never mounted, so there is
   * nothing to blur.
   */
  inline?: boolean;
}

const ExpiredSubscriptionOverlay: React.FC<ExpiredSubscriptionOverlayProps> = ({
  canManageBilling,
  unpaidInvoiceCount,
  outstandingAmount,
  className = 'fixed inset-0',
  code = SUBSCRIPTION_LOCK_CODES.PAYMENT_REQUIRED,
  inline = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const severity = canManageBilling ? 'danger' : 'warning';
  const styles = SEVERITY_STYLES[severity];
  const isUnpaid = code === SUBSCRIPTION_LOCK_CODES.PAYMENT_REQUIRED;

  const handlePayNow = () => {
    // Unpaid invoices are settled on the billing tab; a lapsed plan is replaced on the plans tab.
    navigate(`${APP_ROUTES.BILLING_AND_PLANS}?tab=${isUnpaid ? 'billing' : 'plans'}`);
  };

  // Amounts use the shared formatter (CVA-4738) so the overlay shows the
  // organization's currency, like the rest of the app.
  const billingCopy: Record<SubscriptionLockCode, { title: string; message: string }> = {
    [SUBSCRIPTION_LOCK_CODES.PAYMENT_REQUIRED]: {
      title: tr('g.service_restricted', 'Service Restricted'),
      message: t(
        'subscription_overlay.restricted',
        'Service Restricted: {{count}} overdue invoice(s) totaling {{amount}}. Your access has been restricted. Please settle all outstanding invoices immediately to restore full access.',
        { count: unpaidInvoiceCount, amount: formatCurrency(outstandingAmount) },
      ),
    },
    [SUBSCRIPTION_LOCK_CODES.TRIAL_EXPIRED]: {
      title: t('subscription_overlay.trial_ended_title', 'Free Trial Ended'),
      message: t(
        'subscription_overlay.trial_ended',
        "Your institute's free trial has ended. Choose a plan to restore full access. Your data is safe and will be available as soon as you subscribe.",
      ),
    },
    [SUBSCRIPTION_LOCK_CODES.CANCELLED]: {
      title: t('subscription_overlay.cancelled_title', 'Subscription Ended'),
      message: t(
        'subscription_overlay.cancelled',
        "Your institute's subscription was cancelled and the paid period has ended. Choose a plan to restore full access. Your data is safe and will be available as soon as you subscribe.",
      ),
    },
  };

  const copy = billingCopy[code ?? SUBSCRIPTION_LOCK_CODES.PAYMENT_REQUIRED];
  const title = canManageBilling
    ? copy.title
    : tr('g.subscription_inactive', 'Subscription Inactive');
  const overlayMessage = canManageBilling
    ? copy.message
    : t(
        'subscription_overlay.inactive',
        "Your institute's subscription is currently inactive. Please contact your school administrator or billing department to restore full access to your workspace tools.",
      );

  const card = (
    <div
      className={
        inline
          ? 'w-full max-w-md rounded-2xl border border-gray-200 bg-white px-6 py-8 shadow-2xl sm:px-8'
          : 'fixed left-1/2 top-1/2 z-[1001] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-gray-200 bg-white px-6 py-8 shadow-2xl sm:px-8'
      }
    >
      <div className="flex flex-col items-center justify-center gap-4 p-1 text-center sm:p-2">
        <div
          className={`aspect-square rounded-full border-2 p-3 shadow-lg ${styles.bgColor} ${styles.iconBorder}`}
        >
          <div className="flex h-full w-full items-center justify-center">
            <i className={`pi pi-lock text-3xl ${styles.iconColor}`} aria-hidden="true" />
          </div>
        </div>

        <h3 className="font-poppins text-lg font-semibold text-gray-800">{title}</h3>

        <p className="max-w-xl font-poppins text-sm leading-6 text-gray-700 sm:text-base">
          {overlayMessage}
        </p>

        {canManageBilling && (
          <div className="mt-2 flex w-full justify-center">
            <AppButton
              data-testid="subscription-lock-pay"
              label={
                isUnpaid
                  ? t('common.pay_now', 'Pay Now')
                  : t('billing.banner.view_plans', 'View Plans')
              }
              onClick={handlePayNow}
              severity="danger"
            />
          </div>
        )}
      </div>
    </div>
  );

  if (inline) {
    return <div className="flex min-h-[60vh] w-full items-center justify-center p-4">{card}</div>;
  }

  return (
    <div
      className={`${className} z-[1] flex h-full items-center justify-center bg-white/45 backdrop-blur-sm`}
    >
      {card}
    </div>
  );
};

export type { ExpiredSubscriptionOverlayProps };
export default ExpiredSubscriptionOverlay;
